import { useId } from "react";
import { Label } from "@/components/ui/label";
import type { PrinterOption } from "@/lib/printer-options";

export interface LabelPrinterPickerProps {
  options: PrinterOption[];
  /** null = the agent's own default queue, which is a real choice and not "nothing". */
  value: number | null;
  onChange: (printerId: number | null) => void;
  /**
   * The chosen printer resolves to no real queue (deleted, or the list failed to load).
   * Says so on screen as well as being refused at submit: the operator should see why the
   * print will not go before they click, not after.
   */
  unresolved: boolean;
}

/**
 * The destination picker for the parcel-label dialogs.
 *
 * Its own component so the job and invoice dialogs share one copy of the "displays one printer,
 * prints to another" guard, its wording, and the label/description wiring that makes the warning
 * reachable from the select.
 */
export function LabelPrinterPicker({ options, value, onChange, unresolved }: LabelPrinterPickerProps) {
  const fieldId = useId();
  const warningId = useId();

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldId}>Printer</Label>
      {/* Native select, not shadcn's Select: Radix renders its listbox in a portal with no
          real <option> elements. Deliberately never disabled while the list loads — the
          empty list is usable (it means "the agent's own default"), and disabling would
          take the control away mid-reach. */}
      <select
        id={fieldId}
        className="flex h-9 w-full items-center rounded-md border border-border bg-background px-3 py-2 text-sm"
        value={value ?? ""}
        aria-describedby={unresolved ? warningId : undefined}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      >
        {/* Deliberately does not name the agent: the dialog body already does, and a second
            mention here is what an operator scanning for "where did it go" reads first. */}
        <option value="">&mdash; The agent&rsquo;s default printer &mdash;</option>
        {options.map((p) => (
          <option key={p.printerId} value={p.printerId}>{p.printerName}</option>
        ))}
      </select>
      {unresolved && (
        <p id={warningId} className="text-xs text-destructive">
          This printer can&rsquo;t be confirmed, so the labels can&rsquo;t be sent to it. Pick another, or
          the agent&rsquo;s default printer.
        </p>
      )}
    </div>
  );
}
